/**
 * v0.4 追加要件⑪ 章⑤ (D-365〜D-378): 従業員横断ビュー (`/shift/report/employees`)。
 * 行=従業員 / 列=日付。複数店舗・月またぎ可 (編成ボードとの決定的な違い、読み取り専用)。
 * <768px は従業員ドリル (1人を選び期間カレンダーを出す、U-10)。
 *
 * 設計: docs/design/v04-cross-employee-shift-report-detail.md §6 / §11
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch, ApiClientError } from '@/lib/api';
import { isoToLocalHm } from '@/lib/shift-format';
import { useLabel } from '@/lib/labels';
import { formatCurrency } from '@/lib/format';
import { useHolidays } from '@/hooks/useHolidays';
import { DateLabel } from '@/components/DateLabel';
import { EmptyState } from '@/components/EmptyState';
import { StoreMultiSelect, collectStoreIds } from '@/components/StoreMultiSelect';
import { LaborCostConfidenceToggle } from '@/components/LaborCostConfidenceToggle';
import { useScopeStore, findNodeInTree } from '@/stores/scope';
import { thisMonthStart, nextMonthStart, enumerateDates } from '@/lib/shift-report-dates';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { type ViewColumn, formatViewColumnValue, resolveViewColumnLabel } from '@/lib/shift-report-view-columns';
import { OMITTED_REASON_LABELS, type LaborCostConfidence } from '@/lib/shift-report-labels';

type EmployeeCell = { work_date: string; work_node_id: string; start_at: string; end_at: string; origin: string; status: string };
type AmountDisplayLevel = 'none' | 'status' | 'hours' | 'amount';
type EmployeeRow = {
  employee_id: string | null; external_person_id: string | null;
  display_name: string; home_node_id: string; home_node_name: string;
  employment_type_label: string | null;
  cells: EmployeeCell[];
  totals: {
    assignment_days: number;
    labor_minutes?: number;
    labor_cost_amount?: number;
    contracted_work_days: number | null;
    overtime_minutes: number | null;
    amount_display_level: AmountDisplayLevel;
  };
};
type EmployeesResponse = {
  range: { from: string; to: string };
  confidence: LaborCostConfidence;
  totals_scope: 'visible_cells';
  scope_summary: { node_count: number; person_count: number };
  // v0.4 追加要件⑪ 章⑦フォローアップ (D-348): /settings/report-views の既定ビューに列が
  // 1つでも設定されていれば、既存の固定3列 (出勤日数/実績人時/人件費見込み) の右に追加表示する
  // (null = 追加列なし)。値は employee_id ?? external_person_id ?? display_name をキーとする。
  view_columns: ViewColumn[] | null;
  rows: EmployeeRow[];
};

/** routes/shift-report.ts の targetKey 規約 (employeeId ?? externalPersonId ?? displayName) と一致させる */
const viewColumnTargetKeyOf = (row: EmployeeRow): string => row.employee_id ?? row.external_person_id ?? row.display_name;

const STORE_COLORS = ['#C91F28', '#F05A28', '#F2B705', '#6E80A8', '#1A1A1A', '#8D8D8D'];

// ⚠️ 2026-09-03: ここは `iso.slice(11, 16)` で **UTC の ISO 文字列をそのまま切り出して**おり、
// 全シフトが9時間ずれて表示されていた (自由が丘の早番 08:00〜17:00 が `23:00〜08:00` という
// あり得ない夜勤に見え、user が実機で発見)。時刻の整形は必ず共有ヘルパ isoToLocalHm を通すこと
// (編成ボード・シフト印刷・急な出勤要請は元から共有ヘルパを使っており、本画面だけが逸脱していた)。
const hhmm = isoToLocalHm;

function LaborCostCell({ level, value }: { level: AmountDisplayLevel; value: number | undefined }) {
  if (value === undefined) {
    return <span className="text-text-sub" title={level === 'none' ? '閲覧権限がありません' : '金額の閲覧権限がありません'}>—</span>;
  }
  return <span>{formatCurrency(value)}</span>;
}

export function ShiftReportEmployeesPage() {
  const t = useLabel();
  const scopeRoots = useScopeStore((s) => s.roots);
  const scopeSelectedNodeId = useScopeStore((s) => s.selectedNodeId);
  const scopedNodes = useMemo(() => {
    if (!scopeSelectedNodeId) return scopeRoots;
    const subtree = findNodeInTree(scopeRoots, scopeSelectedNodeId);
    return subtree ? [subtree] : scopeRoots;
  }, [scopeRoots, scopeSelectedNodeId]);

  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set());
  const [from, setFrom] = useState(thisMonthStart());
  const [to, setTo] = useState(nextMonthStart(thisMonthStart()));
  const [confidence, setConfidence] = useState<LaborCostConfidence>('estimate');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<'name' | 'labor_minutes' | 'assignment_days'>('name');
  const [mobilePersonKey, setMobilePersonKey] = useState<string | null>(null);

  const nodeColorIndex = useMemo(() => {
    const ids = scopedNodes.flatMap(collectStoreIds);
    const map = new Map<string, number>();
    ids.forEach((id, idx) => map.set(id, idx));
    return map;
  }, [scopedNodes]);
  const colorOf = (storeId: string) => STORE_COLORS[(nodeColorIndex.get(storeId) ?? 0) % STORE_COLORS.length]!;

  const nodeIdsParam = selectedNodeIds.size > 0 ? [...selectedNodeIds].join(',') : null;
  const { data, isLoading, isError, error, refetch } = useQuery<EmployeesResponse>({
    queryKey: ['shift-report-employees', nodeIdsParam, from, to, confidence, q, sort],
    queryFn: () => apiFetch<EmployeesResponse>(
      `/shift-report/employees?from=${from}&to=${to}&confidence=${confidence}&sort=${sort}`
      + (nodeIdsParam ? `&node_ids=${encodeURIComponent(nodeIdsParam)}` : '')
      + (q ? `&q=${encodeURIComponent(q)}` : ''),
    ),
  });

  const holidays = useHolidays(from, to, scopeSelectedNodeId ?? '');
  const dates = useMemo(() => (data ? enumerateDates(data.range.from, data.range.to) : []), [data]);

  const cellsByPersonDate = useMemo(() => {
    const map = new Map<string, EmployeeCell[]>();
    for (const row of data?.rows ?? []) {
      const key = row.employee_id ?? row.external_person_id ?? '';
      for (const cell of row.cells) {
        const cellKey = `${key}|${cell.work_date}`;
        const list = map.get(cellKey);
        if (list) list.push(cell); else map.set(cellKey, [cell]);
      }
    }
    return map;
  }, [data]);

  const rangeTooLarge = isError && error instanceof ApiClientError && error.code === 'REPORT_RANGE_TOO_LARGE';
  const outOfScope = isError && error instanceof ApiClientError && error.code === 'OUT_OF_SCOPE';

  const rows = data?.rows ?? [];
  const mobilePerson = rows.find((r) => (r.employee_id ?? r.external_person_id) === mobilePersonKey) ?? rows[0];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-h1">{t('entity.shift_report_employees', '従業員横断ビュー')}</h1>
      </div>

      <div className="border border-surface-border rounded-lg bg-white p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-text-sub mb-1" htmlFor="sre-from">開始日</label>
            <input
              id="sre-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="min-h-[44px] px-3 py-2 rounded border border-surface-border text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-text-sub mb-1" htmlFor="sre-to">終了日 (含まない)</label>
            <input
              id="sre-to" type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="min-h-[44px] px-3 py-2 rounded border border-surface-border text-sm"
            />
          </div>
          <LaborCostConfidenceToggle value={confidence} onChange={setConfidence} />
          <div>
            <label className="block text-xs text-text-sub mb-1" htmlFor="sre-q">氏名で絞り込み</label>
            <input
              id="sre-q" type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="山田 太郎"
              className="min-h-[44px] px-3 py-2 rounded border border-surface-border text-sm w-40"
            />
          </div>
          <div>
            <span className="block text-xs text-text-sub mb-1" id="sre-sort-label">並び替え</span>
            <Select value={sort} onValueChange={(v) => setSort(v as typeof sort)}>
              <SelectTrigger aria-labelledby="sre-sort-label" className="w-32 min-h-[44px] text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">氏名</SelectItem>
                <SelectItem value="labor_minutes">実績人時</SelectItem>
                <SelectItem value="assignment_days">出勤日数</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <span className="block text-xs text-text-sub mb-1">対象店舗</span>
            <StoreMultiSelect nodes={scopedNodes} selectedIds={selectedNodeIds} onChange={setSelectedNodeIds} colorOf={colorOf} savedViewNamespace="shift-report-employees" />
          </div>
        </div>
        {data && (
          <div className="text-xs text-text-sub" aria-live="polite">
            対象店舗 {data.scope_summary.node_count} / 対象者 {data.scope_summary.person_count}名
          </div>
        )}
      </div>

      {isLoading && <div className="py-10 text-center text-sm text-text-sub" aria-live="polite">読み込み中…</div>}

      {isError && !rangeTooLarge && !outOfScope && (
        <EmptyState message="従業員横断ビューの取得に失敗しました" description={error instanceof ApiClientError ? error.message : undefined} actionLabel="再読み込み" onAction={() => refetch()} />
      )}
      {isError && outOfScope && (
        <EmptyState message="閲覧可能な範囲がありません" description={error instanceof ApiClientError ? error.message : undefined} />
      )}
      {isError && rangeTooLarge && (
        <EmptyState
          message="指定範囲が大きすぎます"
          description={error instanceof ApiClientError ? error.message : undefined}
          actionLabel="期間を1ヶ月に縮める"
          onAction={() => { setFrom(thisMonthStart()); setTo(nextMonthStart(thisMonthStart())); }}
        />
      )}

      {data && rows.length === 0 && !isError && <EmptyState message="対象者がいません" description="期間・店舗・検索条件を見直してください" />}

      {data && rows.length > 0 && (
        <>
          {/* ≥768px: 行=従業員 / 列=日付、右端に期間合計 */}
          <div className="hidden md:block border border-surface-border rounded-lg bg-white overflow-hidden">
            <p id="sre-legend" className="px-4 pt-3 text-xs text-text-sub">
              <span aria-hidden="true">★</span> = 所属店舗以外での勤務 (応援)
            </p>
            <div className="overflow-x-auto">
              <table className="border-collapse w-full text-sm" aria-describedby="sre-legend">
                <thead>
                  <tr>
                    <th scope="col" className="sticky left-0 z-10 bg-surface-sunken border-b border-r border-surface-border px-3 py-2 text-left text-xs text-text-sub" style={{ minWidth: 160 }}>
                      氏名
                    </th>
                    {dates.map((date) => (
                      <th key={date} scope="col" className="relative border-b border-surface-border px-2 py-2 text-center text-xs whitespace-nowrap" style={{ minWidth: 64 }}>
                        <DateLabel date={date} withWeekday holidays={holidays} />
                      </th>
                    ))}
                    <th scope="col" className="border-b border-l border-surface-border px-2 py-2 text-center text-xs whitespace-nowrap" style={{ minWidth: 70 }}>出勤日数</th>
                    <th scope="col" className="border-b border-surface-border px-2 py-2 text-center text-xs whitespace-nowrap" style={{ minWidth: 80 }}>実績人時</th>
                    <th scope="col" className="border-b border-surface-border px-2 py-2 text-center text-xs whitespace-nowrap" style={{ minWidth: 100 }}>人件費見込み</th>
                    {(data.view_columns ?? []).map((col) => (
                      <th key={col.column_id} scope="col" className="border-b border-l border-surface-border px-2 py-2 text-center text-xs whitespace-nowrap" style={{ minWidth: 90 }}>
                        {resolveViewColumnLabel(t, col)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const key = row.employee_id ?? row.external_person_id ?? '';
                    return (
                      <tr key={key} className="odd:bg-white even:bg-surface-stripe">
                        <th scope="row" className="sticky left-0 z-10 bg-inherit border-r border-surface-border px-3 py-1.5 text-left text-xs whitespace-nowrap">
                          <div className="font-medium text-text-body">{row.display_name}</div>
                          <div className="text-text-sub">{row.home_node_name}{row.employment_type_label ? ` ・ ${row.employment_type_label}` : ''}</div>
                        </th>
                        {dates.map((date) => {
                          const cells = cellsByPersonDate.get(`${key}|${date}`) ?? [];
                          if (cells.length === 0) return <td key={date} className="px-2 py-1.5 text-center text-xs text-text-sub">—</td>;
                          return (
                            <td key={date} className="px-2 py-1.5 text-center text-xs whitespace-nowrap">
                              {cells.map((cell, idx) => (
                                <div key={idx}>
                                  {hhmm(cell.start_at)}〜{hhmm(cell.end_at)}
                                  {cell.work_node_id !== row.home_node_id && (
                                    <span className="text-misespark-navy">
                                      {' '}<span aria-hidden="true">★</span><span className="sr-only">応援</span>
                                    </span>
                                  )}
                                </div>
                              ))}
                            </td>
                          );
                        })}
                        <td className="border-l border-surface-border px-2 py-1.5 text-center text-xs">{row.totals.assignment_days}日</td>
                        <td className="px-2 py-1.5 text-center text-xs">
                          {row.totals.labor_minutes !== undefined ? `${(row.totals.labor_minutes / 60).toFixed(1)}h` : <span className="text-text-sub" title="勤務時間の閲覧権限がありません">—</span>}
                        </td>
                        <td className="px-2 py-1.5 text-center text-xs">
                          <LaborCostCell level={row.totals.amount_display_level} value={row.totals.labor_cost_amount} />
                        </td>
                        {(data.view_columns ?? []).map((col) => (
                          <td key={col.column_id} className="border-l border-surface-border px-2 py-1.5 text-center text-xs whitespace-nowrap">
                            {col.omitted
                              ? <span className="text-text-sub" title={col.omitted_reason ? (OMITTED_REASON_LABELS[col.omitted_reason] ?? col.omitted_reason) : undefined}>非表示</span>
                              : formatViewColumnValue(col.values[viewColumnTargetKeyOf(row)] ?? null, col.format_hint)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* <768px: 従業員ドリル (U-10) */}
          <div className="md:hidden space-y-3">
            <div>
              <span className="block text-xs text-text-sub mb-1" id="sre-person-label">従業員</span>
              <Select
                value={mobilePerson ? (mobilePerson.employee_id ?? mobilePerson.external_person_id ?? '') : undefined}
                onValueChange={(v) => setMobilePersonKey(v)}
              >
                <SelectTrigger aria-labelledby="sre-person-label" className="w-full min-h-[44px] text-sm bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {rows.map((row) => {
                    const key = row.employee_id ?? row.external_person_id ?? '';
                    return <SelectItem key={key} value={key}>{row.display_name}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            {mobilePerson && (
              <div className="border border-surface-border rounded-lg bg-white p-3 space-y-3">
                <div>
                  <div className="font-medium text-sm">{mobilePerson.display_name}</div>
                  <div className="text-xs text-text-sub">{mobilePerson.home_node_name}{mobilePerson.employment_type_label ? ` ・ ${mobilePerson.employment_type_label}` : ''}</div>
                </div>
                <dl className="grid grid-cols-3 gap-2 text-xs">
                  <div className="flex flex-col"><dt className="text-text-sub">出勤日数</dt><dd>{mobilePerson.totals.assignment_days}日</dd></div>
                  <div className="flex flex-col">
                    <dt className="text-text-sub">実績人時</dt>
                    <dd>{mobilePerson.totals.labor_minutes !== undefined ? `${(mobilePerson.totals.labor_minutes / 60).toFixed(1)}h` : '—'}</dd>
                  </div>
                  <div className="flex flex-col">
                    <dt className="text-text-sub">人件費見込み</dt>
                    <dd><LaborCostCell level={mobilePerson.totals.amount_display_level} value={mobilePerson.totals.labor_cost_amount} /></dd>
                  </div>
                  {(data?.view_columns ?? []).map((col) => (
                    <div key={col.column_id} className="flex flex-col">
                      <dt className="text-text-sub">{resolveViewColumnLabel(t, col)}</dt>
                      <dd>
                        {col.omitted
                          ? <span title={col.omitted_reason ? (OMITTED_REASON_LABELS[col.omitted_reason] ?? col.omitted_reason) : undefined}>非表示</span>
                          : formatViewColumnValue(col.values[viewColumnTargetKeyOf(mobilePerson)] ?? null, col.format_hint)}
                      </dd>
                    </div>
                  ))}
                </dl>
                <ul className="divide-y divide-surface-border">
                  {dates.map((date) => {
                    const key = mobilePerson.employee_id ?? mobilePerson.external_person_id ?? '';
                    const cells = cellsByPersonDate.get(`${key}|${date}`) ?? [];
                    return (
                      <li key={date} className="py-1.5 flex items-center justify-between text-xs">
                        <DateLabel date={date} withWeekday holidays={holidays} />
                        <span className="text-text-sub">
                          {cells.length === 0 ? '—' : cells.map((cell) => `${hhmm(cell.start_at)}〜${hhmm(cell.end_at)}${cell.work_node_id !== mobilePerson.home_node_id ? ' (応援)' : ''}`).join(' / ')}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
